import Button from "common/components/Button/Button";

import { Sector } from "types/Sector";
import { Link } from "react-router-dom";
import useAuth from "common/hooks/useAuth";
import { useTranslation } from "react-i18next";
import { TbFence } from "react-icons/tb";
import TooltipWrapper from "common/components/TooltipWrapper/TooltipWrapper";
import { Enclosure } from "types/Enclosure";
import { User } from "types";
import { UserIcon } from "@heroicons/react/24/outline";
import { useMemo } from "react";
import _ from "lodash";
import { Specy } from "types/Specy";

function SectorItem({ item, onDelete, onNavigate }: any) {
  const { user: currentUser } = useAuth();
  const canSeeDetailsFromItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.sector?.read)) ??
    false;
  const canEditItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.sector?.update)) ??
    false;
  const canDeleteItem =
    (currentUser?.isSuperadmin ||
      (currentUser?.isAdmin &&
        Boolean(currentUser?.park.id === item.park.id)) ||
      ((currentUser?.isManager || currentUser?.isObserver) &&
        Boolean(currentUser?.park.id === item.park.id) &&
        currentUser?.administrationPermissions?.sector?.delete)) ??
    false;

  const { name, description, enclosures, managers } = item as Sector;

  const editLink = `/sectors/edit/${item.id}`;

  const { t } = useTranslation();

  const species = useMemo(() => {
    const items = enclosures?.flatMap((enclosure) => [
      ...(enclosure.animals ?? []).map((animal) => animal.species),
      ...(enclosure.observationGroups ?? []).flatMap((group) =>
        (group.animals ?? []).map((animal) => animal.species)
      ),
    ]);
    const itemsIds = _.uniq(items?.filter((i) => i).map((i) => i?.id));
    return itemsIds?.map((i) => items.find((a) => a?.id === i));
  }, [enclosures]);

  return (
    <div className="flex px-4 py-4 mt-2 w-full relative bg-white cursor-pointer rounded-lg border border-slate-150 transition-all">
      <Link
        to={editLink}
        className={
          !canSeeDetailsFromItem
            ? "pointer-events-none flex flex-1"
            : "flex flex-1"
        }
      >
        <div className="flex flex-col flex-1 justify-between pl-4 pr-6 mb-2">
          <div className="flex mt-4 mb-2 gap-3">
            <div className="flex-1">
              <h4 className="text-xl font-bold text-primary leading-4 mb-2">
                {name}
              </h4>
              <div className="flex items-center text-sm">
                <span className="font-bold">{description}</span>
              </div>
            </div>
          </div>
        </div>
      </Link>

      <div className="flex flex-col justify-between items-end text-xs lg:border-l pl-4 lg:min-w-[22em] border-slate-150 gap-2">
        <TooltipWrapper
          className="mr-1 my-1"
          content={
            enclosures?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {enclosures?.map((e: Enclosure) => (
                  <span className="font-bold">{e.name}</span>
                ))}
              </div>
            ) : null
          }
        >
          <div className="flex items-center gap-1 text-sm">
            <TbFence className="w-3 h-3" />
            <span className="font-bold">
              {t("forms.enclosuresLength", { count: enclosures?.length || 0 })}
            </span>
          </div>
        </TooltipWrapper>
        <TooltipWrapper
          className="mr-1 my-1"
          content={
            managers?.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {managers?.map((e: User) => (
                  <span className="font-bold">{e.username}</span>
                ))}
              </div>
            ) : null
          }
        >
          <div className="flex items-center gap-1 text-sm">
            <UserIcon className="w-3 h-3" />
            <span className="font-bold">
              {managers?.length > 1
                ? t("forms.managersLength", {
                    count: managers?.length || 0,
                  })
                : t("forms.managerLength", {
                    count: managers?.length || 0,
                  })}
            </span>
          </div>
        </TooltipWrapper>

        <TooltipWrapper
          className="mr-1 my-1"
          content={
            species?.length > 0 ? (
              <div className="flex flex-col flex-wrap gap-1">
                {species?.map((e: Specy | undefined) =>
                  e ? (
                    <span className="font-bold">
                      {e.scientificName || e.name}
                    </span>
                  ) : null
                )}
              </div>
            ) : null
          }
        >
          <div className="flex items-center gap-1 text-sm">
            <UserIcon className="w-3 h-3" />
            <span className="font-bold">
              {species?.length > 1
                ? t("forms.speciesLength", {
                    count: species?.length || 0,
                  })
                : t("forms.specyLength", {
                    count: species?.length || 0,
                  })}
            </span>
          </div>
        </TooltipWrapper>

        <div className="flex w-full items-center justify-end text-slate-500 gap-1">
          <TooltipWrapper content={<span>{t("actions.delete")}</span>}>
            <Button
              type="error-line"
              icon="TrashIcon"
              compact
              onClick={() => onDelete(item.id)}
              disabled={!canDeleteItem}
              confirmMessage={t("actions.confirmDelete")}
            />
          </TooltipWrapper>

          <Button
            title={t("actions.edit")}
            type="warning-line"
            icon="PencilSquareIcon"
            onClick={() => onNavigate(item.id)}
            disabled={!canEditItem}
          />
        </div>
      </div>
    </div>
  );
}

export default SectorItem;
